import React, { useState, useCallback } from 'react';
import { Paper, Title, Text, Stack, Button, Alert, Group, Box } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDatabase, IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import { OsmImportSectionProps } from './OsmImportSection.types';
import { RegionSelect } from '../../domain/RegionSelect';
import { SubAreaSelect } from '../../domain/SubAreaSelect';
import { OsmImportHistory } from './OsmImportHistory';
import { useTriggerOsmImportMutation } from '../../../../application/queries/adminOperationsQueries';
import { useAuth } from '../../../hooks/useAuth';

/**
 * OsmImportSection Component
 *
 * Admin-only section for manually triggering OpenStreetMap data imports.
 * Allows selecting a specific Region and SubArea, then importing all
 * Places, Amenities, and Businesses for that geographic area.
 *
 * Features:
 * - Region and SubArea selection
 * - Confirmation modal before import
 * - Real-time import progress (via mutation state)
 * - Import success statistics display
 * - Error handling with user-friendly messages
 * - Import history table
 *
 * @example
 * <OsmImportSection />
 */
export const OsmImportSection: React.FC<OsmImportSectionProps> = (): React.ReactElement => {
  const { user } = useAuth();
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [selectedSubAreaId, setSelectedSubAreaId] = useState<string | null>(null);

  // Trigger import mutation
  const triggerImport = useTriggerOsmImportMutation();

  /**
   * Handle region selection change
   * Resets SubArea selection when region changes
   */
  const handleRegionChange = useCallback((regionId: string | null): void => {
    setSelectedRegionId(regionId);
    setSelectedSubAreaId(null); // Reset SubArea when Region changes
  }, []);

  /**
   * Handle SubArea selection change
   */
  const handleSubAreaChange = useCallback((subAreaId: string | null): void => {
    setSelectedSubAreaId(subAreaId);
  }, []);

  /**
   * Handle import button click
   * Shows confirmation modal before triggering import
   */
  const handleStartImport = useCallback((): void => {
    console.log('🔍 Debug - handleStartImport called', {
      selectedSubAreaId,
      selectedRegionId,
      userRole: user?.role,
      user
    });

    if (!selectedSubAreaId || !selectedRegionId || !user?.role) {
      console.error('❌ Missing required data:', {
        hasSubAreaId: !!selectedSubAreaId,
        hasRegionId: !!selectedRegionId,
        hasUserRole: !!user?.role
      });
      return;
    }

    // Check if "All SubAreas" is selected
    const isAllSubAreas = selectedSubAreaId === '__ALL__';
    console.log('✅ Opening confirmation modal', { isAllSubAreas });

    modals.openConfirmModal({
      title: isAllSubAreas ? 'Confirm Import for All SubAreas' : 'Confirm OSM Data Import',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            {isAllSubAreas
              ? 'This will import OpenStreetMap data (Places, Amenities, Businesses) for ALL SubAreas in the selected region.'
              : 'This will import OpenStreetMap data (Places, Amenities, Businesses) for the selected SubArea.'}
          </Text>
          <Text size="sm" fw={600}>
            {isAllSubAreas
              ? 'This process may take considerable time as it will import data for multiple areas.'
              : 'This process may take several minutes depending on the size of the area.'}
          </Text>
          <Text size="sm" c="dimmed">
            Do you want to continue?
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Start Import', cancel: 'Cancel' },
      confirmProps: { color: 'blue', leftSection: <IconDatabase size={16} /> },
      onConfirm: (): void => {
        triggerImport.mutate({
          subAreaId: isAllSubAreas ? null : selectedSubAreaId,
          regionId: isAllSubAreas ? selectedRegionId : undefined,
          userRole: user.role
        });
      }
    });
  }, [selectedSubAreaId, selectedRegionId, user?.role, triggerImport]);

  // Disable "Start Import" button conditions
  const isImportDisabled = !selectedSubAreaId || triggerImport.isPending;

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Section Header */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconDatabase size={24} stroke={1.5} />
            <Title order={2}>OpenStreetMap Data Import</Title>
          </Group>
          <Text c="dimmed" size="sm">
            Import business data from OpenStreetMap for a specific geographic area
          </Text>
        </Box>

        {/* Region & SubArea Selectors */}
        <Stack gap="sm">
          <RegionSelect
            value={selectedRegionId}
            onChange={handleRegionChange}
            label="Select Region"
            description="Choose the region containing the area to import"
            required
          />

          <SubAreaSelect
            regionId={selectedRegionId}
            value={selectedSubAreaId}
            onChange={handleSubAreaChange}
            label="Select SubArea"
            description="Choose the specific subarea to import data for"
            required
          />
        </Stack>

        {/* Start Import Button */}
        <Button
          size="md"
          leftSection={<IconDatabase size={16} />}
          onClick={handleStartImport}
          disabled={isImportDisabled}
          loading={triggerImport.isPending}
        >
          {triggerImport.isPending ? 'Importing Data...' : 'Start Import'}
        </Button>

        {/* Success Display */}
        {triggerImport.isSuccess && triggerImport.data && (
          <Alert
            icon={<IconCheck size={16} />}
            title="Import Completed Successfully"
            color="green"
            variant="light"
          >
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Import Statistics:
              </Text>
              <Group gap="xl">
                <Box>
                  <Text size="sm" c="dimmed">
                    Places:
                  </Text>
                  <Text size="sm" fw={600}>
                    {triggerImport.data.placesImported} imported, {triggerImport.data.placesUpdated} updated
                  </Text>
                </Box>
                <Box>
                  <Text size="sm" c="dimmed">
                    Amenities:
                  </Text>
                  <Text size="sm" fw={600}>
                    {triggerImport.data.amenitiesImported} imported, {triggerImport.data.amenitiesUpdated} updated
                  </Text>
                </Box>
                <Box>
                  <Text size="sm" c="dimmed">
                    Businesses:
                  </Text>
                  <Text size="sm" fw={600}>
                    {triggerImport.data.businessesImported} imported, {triggerImport.data.businessesUpdated} updated
                  </Text>
                </Box>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                Duration: {triggerImport.data.duration}
              </Text>
            </Stack>
          </Alert>
        )}

        {/* Error Display */}
        {triggerImport.isError && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Import Failed"
            color="red"
            variant="light"
          >
            <Text size="sm">
              {triggerImport.error?.message || 'An error occurred while importing data. Please try again.'}
            </Text>
          </Alert>
        )}

        {/* Import History */}
        <OsmImportHistory />
      </Stack>
    </Paper>
  );
};
